import { CommandResult, Environment, GridDefinition } from "Service/Client";
import { Grid } from "./Grid";
import { GridSettings } from "Configuration";
import { CommandService, DatabaseImageService, DropDownService, ExportService, GridDefinitionService, TextBlockService } from "Service";
import { DataConstants } from "Constants";
import CancellationToken from "cancellationtoken";
import { LOAD_IDENTIFIER_COLUMN_NAME } from "Grids";
import { Util } from "Core";

export class TreeGrid extends Grid {
    // protected _kendoElement: kendo.ui.TreeList;

    private dataSourceOptions: kendo.data.DataSourceOptions;
    private loadIdentifiers: { [identifier: number]: number } = {};
    private expandedElements: { [hash: string]: boolean } = {};
    private currentLoadIdentifier: number = 0;

    /**
     * Create a new EasyEdit.
     * @param gridDefinition
     * @param gridSettings
     * @param comboboxService
     * @param commandService
     * @param exportService
     * @param textBlockService
     * @hidden
     */
    constructor(
        gridDefinition: GridDefinition,
        textBlockCategory: string,
        gridSettings: GridSettings,
        environment: Environment,
        comboboxService: DropDownService,
        commandService: CommandService,
        exportService: ExportService,
        gridDefinitionService: GridDefinitionService,
        textBlockService: TextBlockService,
        databaseImageService: DatabaseImageService
    ) {
        super(
            gridDefinition,
            textBlockCategory,
            gridSettings,
            environment,
            comboboxService,
            commandService,
            exportService,
            gridDefinitionService,
            textBlockService,
            databaseImageService
        );
        this.isAutoResize = true;
        this.gridDefinition.AddAllowed = false;
    }

    /**
     * Sets the jQuery-Element to initialize the grid.
     * @param element JQuery-Wrapper in which the grid is initialized.
     * @hidden
     */
    protected async setElementInternal(element: JQuery): Promise<void> {
        await this.textBlockService.getAll();

        this.userConfig = undefined;
        if (this.currentUserGridConfig && this.currentUserGridConfig.Configuration) {
            try {
                this.userConfig = JSON.parse(this.currentUserGridConfig.Configuration);
            } catch (e) {
                console.error("error in user grid configuration: " + e + " " + this.currentUserGridConfig.Configuration);
            }
        }

        const definition = this.getColumnsAndFields(this.userConfig);
        definition.fields["__dms-expander"] = { type: "string", nullable: true };
        definition.fields[DataConstants.ROW_GUID_COLUMN_NAME] = { type: "string", nullable: false };
        definition.fields[DataConstants.PARENT_ROW_GUID_COLUMN_NAME] = { type: "string", nullable: true };

        if (!definition.fields[this.gridDefinition.PrimaryKeyColumnName]) {
            definition.fields[this.gridDefinition.PrimaryKeyColumnName] = { type: "string", nullable: false };
        }

        definition.fields[DataConstants.PARENT_ID] = { type: "string", nullable: true, defaultValue: null };
        definition.fields[this.gridDefinition.HasChildNodesColumnName] = { type: "boolean", nullable: false };

        const expanderColumn: kendo.ui.TreeListColumn = {
            field: "__dms-expander",
            title: " ",
            width: "100px",
            expandable: true,
            editable: () => false,
        };

        definition.columns.unshift(expanderColumn);

        this.dataSourceOptions = {
            transport: {
                read: (options: kendo.data.DataSourceTransportRead) => {
                    this.setBusy(true);
                    if (options.data.sort) {
                        for (let sortIdx = 0, sortLen = options.data.sort.length; sortIdx < sortLen; sortIdx++) {
                            let sortDefinition = options.data.sort[sortIdx];
                            let columnName = <string>sortDefinition.field;
                            let fieldName = this.firstTextColumnForColumnName[columnName];
                            if (fieldName) {
                                sortDefinition.field = fieldName;
                            }
                        }
                    }

                    this.commandOption = options.data;

                    let rowGuid = options.data.id ?? null;
                    let currentLoadIdentifier: number;
                    if (options.data[LOAD_IDENTIFIER_COLUMN_NAME] != null) {
                        currentLoadIdentifier = options.data[LOAD_IDENTIFIER_COLUMN_NAME];
                    } else if (rowGuid) {
                        let nodeModel = this._kendoElement.dataSource.get(rowGuid);
                        currentLoadIdentifier = nodeModel.get(LOAD_IDENTIFIER_COLUMN_NAME);
                        rowGuid = nodeModel.get(DataConstants.ROW_GUID_COLUMN_NAME);
                    } else {
                        currentLoadIdentifier = this.currentLoadIdentifier;
                    }
                    this.loadIdentifiers[currentLoadIdentifier]++;

                    if (options.data.id) {
                        options.data.reloadFromDataSource = true;
                    }
                    const parentRowGuid = this.getParentGuid(options.data.id);
                    // this.commandService
                    //     .getTree(this.gridSettings.dataUrlGet, this.gridDefinition.GridID, rowGuid, this.getParentGuid(rowGuid))
                    // .then((result) => {
                    this.commandService
                        .get(this.gridDefinition.GridID, options.data, this.cancellationTokenSource.token, rowGuid, parentRowGuid)
                        .then((result) => {
                            if (currentLoadIdentifier == this.currentLoadIdentifier) {
                                this.storeOriginalDataRow(result.value);
                                for (let idx = 0, len = result.value.length; idx < len; idx++) {
                                    let row = result.value[idx];
                                    row[LOAD_IDENTIFIER_COLUMN_NAME] = currentLoadIdentifier;
                                    row[DataConstants.PARENT_ROW_GUID_COLUMN_NAME] = rowGuid;
                                    row[DataConstants.PARENT_ID] = options.data.id ?? null;
                                    row.hasChildren = row[this.gridDefinition.HasChildNodesColumnName];
                                    // row.expanded = true;
                                    // expand if node was expanded before treereload
                                    // if (this.gridDefinition.ExpandedColumnName) {
                                    //     row.expanded = row[this.gridDefinition.ExpandedColumnName];
                                    // }
                                    // row.expanded = true;
                                    // if (this.expandedElements.hasOwnProperty(row[DataConstants.ROW_HASH_COLUMN_NAME])) {
                                    //     row.expanded = this.expandedElements[row[DataConstants.ROW_HASH_COLUMN_NAME]];
                                    // }
                                }

                                (options.success as JQuery.Ajax.SuccessCallback<any>)(result, null, null);
                            } else {
                                this.loadIdentifiers[currentLoadIdentifier]--;
                            }
                        });
                },
                create: function (options: kendo.data.DataSourceTransportCreate) {
                    (options.success as JQuery.Ajax.SuccessCallback<any>)(null, null, null);
                },
                update: function (options: kendo.data.DataSourceTransportUpdate) {
                    (options.success as JQuery.Ajax.SuccessCallback<any>)(null, null, null);
                },
                destroy: function (options: kendo.data.DataSourceTransportDestroy) {
                    (options.success as JQuery.Ajax.SuccessCallback<any>)(null, null, null);
                },
            },
            schema: {
                data: function (data: CommandResult) {
                    return data.value;
                },
                total: function (data: CommandResult) {
                    return data["odata.count"];
                },
                model: {
                    fields: definition.fields,
                    parentId: DataConstants.PARENT_ID,
                    id: this.gridDefinition.PrimaryKeyColumnName,
                },
                errors: "error",
            },
            serverPaging: true,
            serverSorting: true,
            serverFiltering: true,
            serverGrouping: true,
            serverAggregates: true,
            batch: false,
            sort: definition.sortingFields,
            error: (e) => this.onError(e),
            autoSync: false,
        };

        if (this.gridDefinition.FormatDefinitions && this.gridDefinition.FormatDefinitions.length != 0) {
            let formatDefinitions = this.gridDefinition.FormatDefinitions;
            $.extend(this.dataSourceOptions.schema.model, {
                __dms_rowCssClass: function () {
                    return Util.getFormatCondition(this, formatDefinitions);
                },
            });
        }

        if (definition.modelExtensions) {
            $.extend(this.dataSourceOptions.schema.model, definition.modelExtensions);
        }

        let options: kendo.ui.TreeListOptions = {
            dataSource: new kendo.data.TreeListDataSource(this.dataSourceOptions),
            autoBind: false,
            toolbar: this.getToolbar(),
            editable: {
                mode: "inline",
            },
            navigatable: false,
            sortable: {
                mode: this.gridDefinition.CanSortByMultipleColumns ? "multiple" : "single",
            },
            reorderable: this.gridDefinition.CanReorderColumns || false,
            resizable: this.gridDefinition.CanResizeColumns || false,
            // filterable: definition.filterable
            //     ? {
            //           mode: "menu",
            //           messages: {
            //               filter: this.textBlockService.getChecked(filterTbs, "Apply", "filteroptions"),
            //               and: this.textBlockService.getChecked(filterTbs, "And", "filteroptions"),
            //               clear: this.textBlockService.getChecked(filterTbs, "Clear", "filteroptions"),
            //               info: this.textBlockService.getChecked(filterTbs, "Info", "filteroptions"),
            //               isFalse: this.textBlockService.getChecked(filterTbs, "IsFalse", "filteroptions"),
            //               isTrue: this.textBlockService.getChecked(filterTbs, "IsTrue", "filteroptions"),
            //               or: this.textBlockService.getChecked(filterTbs, "Or", "filteroptions"),
            //               search: this.textBlockService.getChecked(filterTbs, "Search", "filteroptions"),
            //               selectedItemsFormat: this.textBlockService.getChecked(filterTbs, "SelectedItemsFormat", "filteroptions"),
            //               operator: this.textBlockService.getChecked(filterTbs, "Operator", "filteroptions"),
            //               value: this.textBlockService.getChecked(filterTbs, "Value", "filteroptions"),
            //               checkAll: this.textBlockService.getChecked(filterTbs, "CheckAll", "filteroptions"),
            //           },
            //           operators: {
            //               string: {
            //                   eq: this.textBlockService.getChecked(filterTbs, "EqualTo", "filteroptions"),
            //                   neq: this.textBlockService.getChecked(filterTbs, "NotEqualTo", "filteroptions"),
            //                   isnull: this.textBlockService.getChecked(filterTbs, "IsNull", "filteroptions"),
            //                   isnotnull: this.textBlockService.getChecked(filterTbs, "NotIsNull", "filteroptions"),
            //                   isempty: this.textBlockService.getChecked(filterTbs, "IsEmpty", "filteroptions"),
            //                   isnotempty: this.textBlockService.getChecked(filterTbs, "NotIsEmpty", "filteroptions"),
            //                   startswith: this.textBlockService.getChecked(filterTbs, "StartsWith", "filteroptions"),
            //                   contains: this.textBlockService.getChecked(filterTbs, "Contains", "filteroptions"),
            //                   doesnotcontain: this.textBlockService.getChecked(filterTbs, "DoesNotContain", "filteroptions"),
            //                   endswith: this.textBlockService.getChecked(filterTbs, "EndsWith", "filteroptions"),
            //               },
            //               number: {
            //                   eq: this.textBlockService.getChecked(filterTbs, "EqualTo", "filteroptions"),
            //                   neq: this.textBlockService.getChecked(filterTbs, "NotEqualTo", "filteroptions"),
            //                   isnull: this.textBlockService.getChecked(filterTbs, "IsNull", "filteroptions"),
            //                   isnotnull: this.textBlockService.getChecked(filterTbs, "NotIsNull", "filteroptions"),
            //                   gte: this.textBlockService.getChecked(filterTbs, "GreaterThanOrEqualTo", "filteroptions"),
            //                   gt: this.textBlockService.getChecked(filterTbs, "GreaterThan", "filteroptions"),
            //                   lte: this.textBlockService.getChecked(filterTbs, "LessThanOrEqualTo", "filteroptions"),
            //                   lt: this.textBlockService.getChecked(filterTbs, "LessThan", "filteroptions"),
            //               },
            //               date: {
            //                   eq: this.textBlockService.getChecked(filterTbs, "EqualTo", "filteroptions"),
            //                   neq: this.textBlockService.getChecked(filterTbs, "NotEqualTo", "filteroptions"),
            //                   isnull: this.textBlockService.getChecked(filterTbs, "IsNull", "filteroptions"),
            //                   isnotnull: this.textBlockService.getChecked(filterTbs, "NotIsNull", "filteroptions"),
            //                   gte: this.textBlockService.getChecked(filterTbs, "AfterThanOrEqualTo", "filteroptions"),
            //                   gt: this.textBlockService.getChecked(filterTbs, "AfterThan", "filteroptions"),
            //                   lte: this.textBlockService.getChecked(filterTbs, "BeforeThanOrEqualTo", "filteroptions"),
            //                   lt: this.textBlockService.getChecked(filterTbs, "BeforeThan", "filteroptions"),
            //               },
            //           },
            //       }
            //     : false,
            // filter: (e) => this.onFilter(e),
            change: (e) => this.onChange(e.sender.select()),
            edit: (e) => this.onEdit(e.container, e.model),
            scrollable: true,
            columns: <any>definition.columns,
            dataBound: () => this.onDataBound(),
            expand: (e) => this.onExpand(e),
            collapse: (e) => this.onCollapse(e),
            // dataBinding: (e) => this.onTreeListDataBinding(e),
            columnResize: (e) => this.onColumnResize(),
            selectable: "row",
            // noRecords: true,
            height: this.height || 1,
            messages: {
                // noRecords: this.textBlockService.get("advancedGridNoRecordsText", this.textBlockCategory),
            },
            // columnResizeHandleWidth: 5,
        };
        (<any>options).sort = (e: kendo.ui.GridSortEvent) => this.onSort(e);
        this._kendoElement = <any>new kendo.ui.TreeList(this.jQueryElement[0], options);

        const gridHeader = $(`<div class="dms-grid-header"></div>`);
        this.jQueryElement.prepend(gridHeader);

        const toolbar = this._kendoElement.wrapper.find(">.k-grid-toolbar");
        if (toolbar.length > 0) {
            gridHeader.append(toolbar);
            gridHeader.addClass("k-grid-toolbar"); // add class to the header for kendo resize method
        }
        this.initializeToolbar();

        // attach the double click handler on normal table rows which are not editable
        this.jQueryElement.on("dblclick", "tr[data-uid]:not(.k-grid-edit-row)", (e) => this.onRowDoubleClick(e));

        this.jQueryElement.find(".dms-helptext").on("click", (e) => {
            e.stopPropagation();
            e.preventDefault();
            this.tooltipExtension.showHelpTextForElement($(e.target));
        });
    }

    // protected onEditTreeList(e: kendo.ui.TreeListEditEvent) {
    //     super.onEdit(<kendo.ui.GridEditEvent>(<unknown>e));
    // }

    protected onSort(e: kendo.ui.GridSortEvent): void {
        this.currentLoadIdentifier++;
        this.loadIdentifiers[this.currentLoadIdentifier] = 0;

        super.onSort(e);
    }

    /**
     * Set the height of the grid.
     * @param height Height in pixel.
     * @since 4.0.0
     */
    public setHeight(height: string | number): void {
        super.setHeight(height);
        if (this._kendoElement) {
            this._kendoElement.wrapper.height(height);
            this._kendoElement.options.height = height;
            this._kendoElement.resize(true);
        }
    }

    /**
     * Triggers when Node is expanded
     * @param e
     */
    private onExpand(e: kendo.ui.TreeListExpandEvent): void {
        this.expandedElements[e.model.get(this.gridDefinition.PrimaryKeyColumnName)] = true;
        setTimeout(() => this.applyGridFormat());
    }

    /**
     * Triggers when Node is collapsed
     * @param e
     */
    private onCollapse(e: kendo.ui.TreeListCollapseEvent): void {
        delete this.expandedElements[e.model.get(this.gridDefinition.PrimaryKeyColumnName)];
        setTimeout(() => this.applyGridFormat());
    }

    protected onDataBound(): void {
        super.onDataBound();

        if (Object.keys(this.expandedElements).length > 0) {
            for (let key in this.expandedElements) {
                if (this.expandedElements[key]) {
                    let model = this._kendoElement.dataSource.get(key);
                    if (model) {
                        (<kendo.ui.TreeList>(<any>this._kendoElement)).expand(<any>model);
                    }
                }
            }
        }
    }

    /**
     * Get the parent RowGuid for the current item.
     * @param rowGuid RowGuid of the current item.
     */
    private getParentGuid(rowGuid: string): string {
        // get parentRowGuid. the server needs to know in which dataset to look for the row.
        let parentRowGuid: string = null;
        if (this._kendoElement != null) {
            // tree is not initialized yet in first request
            // get current node
            let nodeModel = this._kendoElement.dataSource.get(rowGuid);
            if (nodeModel) {
                // let node = this._kendoElement.dataSource.getByUid(nodeModel.uid);
                return nodeModel.get(DataConstants.PARENT_ROW_GUID_COLUMN_NAME);
                // get parent node
                // let parent = (<kendo.ui.TreeList>this._kendoElement).dataSource..dataSource.parent(node);
                // let dataItem = this._kendoElement.dataItem(parent);
                // if (dataItem) {
                //     parentRowGuid = dataItem.get(DataConstants.ROW_GUID_COLUMN_NAME);
                // }
            }
        }
        return parentRowGuid;
    }

    /**
     * Reloads data of this grid.
     * @hidden
     */
    protected reloadInternal(): Promise<void> {
        this.disposeEditContext();
        // this.resetInsertRowModel();
        this.setBusy(true);
        // this.resetColumnFilters(); // recreate filter menus to reload data when reopening the menus
        // this.scrollPos = this._kendoElement.element.scrollTop();
        this.show();
        this.currentLoadIdentifier++;
        this.loadIdentifiers[this.currentLoadIdentifier] = 0;

        this.cancellationTokenSource = CancellationToken.create();

        return Promise.resolve(
            this._kendoElement.dataSource.read({ [LOAD_IDENTIFIER_COLUMN_NAME]: this.currentLoadIdentifier, reloadFromDataSource: true })
        );
        // return Promise.resolve(this._kendoElement.dataSource.read({ [LOAD_IDENTIFIER_COLUMN_NAME]: this.currentLoadIdentifier }));
    }
}
